import { useState, useRef, useContext, useEffect } from 'react';
import { BlogHeader } from '../components/BlogWriter/BlogHeader';
import { BlogEditor } from '../components/BlogWriter/BlogEditor';
import { EditPanel } from '../components/BlogWriter/EditPanel';
import { EditToolbar } from '../components/BlogWriter/EditToolbar';
import Quill from 'quill';
import { BlogContext } from '../BlogContext';
import { useLocation, useNavigate } from 'react-router-dom';
import HistoryPanel from '../components/BlogWriter/HistoryPanel';
import blogService from '../services/blogApi';
import { toast } from 'react-hot-toast';
import { BlogContent } from '../types';
import remarkHtml from 'remark-html';
import remarkMdx from 'remark-mdx';
import remarkParse from 'remark-parse';
import { unified } from 'unified';
import ImageDeleteWarning from '../components/Modals/ImageDeleteWarning';
import NewBlogWarning from '../components/Modals/NewBlogWarning';
import { Button } from 'components/ui/button';
import BlogSaveAs from '../components/Modals/blogSaveAs';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import CheckAuthModel from '../components/Modals/CheckAuthModel';

const DEFAULT_BLOG_CONTENT: BlogContent = {
  title: '',
  content: '',
  markDownContent: '',
  images: [],
  prevState: '',
  wordCount: 0,
  settings: {
    minLength: '0',
    maxLength: '1000',
    language: 'English',
    toneOfVoice: 'Professional',
    industry: 'General',
    imageCount: 0,
  },
};

export default function BlogWriterPage() {
  const [isEditPanelOpen, setIsEditPanelOpen] = useState(false);
  const [isHistoryPanelOpen, setIsHistoryPanelOpen] = useState(false);
  const [wordCount, setWordCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [activeFormats, setActiveFormats] = useState<{
    [key: string]: boolean | number | string | unknown;
  }>({});
  const location = useLocation();
  const [visibleModel, setVisibleModel] = useState<boolean>(true);
  const [visibleEditModel, setVisibleEditModel] = useState<boolean>(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const isAuthenticated = useSelector(
    (state: RootState) => state.auth.isAuthenticated
  );
  const navigate = useNavigate();
  const {
    blogContent,
    isSettingBlog,
    handleUpdateBlogContent,
    handleUpdateHtmlDump,
    isGenerating,
  } = useContext(BlogContext);

  // Add a new state to store the selection
  const [savedSelection, setSavedSelection] = useState<{
    index: number;
    length: number;
  } | null>(null);

  // Add navigation check
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const blogId = searchParams.get('id');
    const blogContext = localStorage.getItem('blogContext');
    if (!blogId && !blogContext && !isGenerating && !blogContent?.title) {
      navigate('/ai-blog-writer/get-started');
    }
  }, [location.search, isGenerating, blogContent, navigate]);

  const quillRef = useRef<Quill | null>(null);
  // const navigate = useNavigate();

  const handlEditPanel = (open: boolean) => {
    if (isAuthenticated) {
      // Save the current selection before opening the panel
      if (open && quillRef.current) {
        const selection = quillRef.current.getSelection();
        if (selection) {
          setSavedSelection(selection);
        }
      }

      setIsEditPanelOpen(open);
      if (!isEditPanelOpen) {
        setVisibleEditModel(true);
      }

      // Restore the selection after the modal closes
      if (!open && savedSelection && quillRef.current) {
        setTimeout(() => {
          quillRef.current?.setSelection(
            savedSelection.index,
            savedSelection.length
          );
        }, 0);
      }
    } else {
      setShowAuthModal(true);
    }
  };

  // Add useEffect to handle modal visibility changes
  useEffect(() => {
    if (!visibleEditModel && savedSelection && quillRef.current) {
      setTimeout(() => {
        quillRef.current?.setSelection(
          savedSelection.index,
          savedSelection.length
        );
      }, 0);
    }
  }, [visibleEditModel, savedSelection]);

  useEffect(() => {
    const fetchBlogIfNeeded = async () => {
      const searchParams = new URLSearchParams(location.search);
      const blogId = searchParams.get('id');
      const blogSubId = searchParams.get('subId');
      const blogContext = localStorage.getItem('blogContext');
      if (blogContext) {
        handleUpdateBlogContent(JSON.parse(blogContext));
        localStorage.removeItem('blogContext');
      }
      if (blogId) {
        if (!isAuthenticated) {
          setIsLoading(false);
          setShowAuthModal(true);
          return;
        }

        try {
          setIsLoading(true);
          const response = await blogService.getBlog(Number(blogId));
          if (response.success && response.data) {
            // First convert the markdown content to HTML
            try {
              // const htmlResponse = await blogService.convertToPlain({
              //   context: response.data.context,
              // });
              if (!response?.data?.blog) {
                return null;
              }
              console.log(response);
              const htmlString = await unified()
                .use(remarkParse)
                .use(remarkMdx)
                .use(remarkHtml)
                .process(response?.data?.blog?.context);
              console.log(response.data);
              handleUpdateBlogContent({
                id: response.data.id,
                title: String(response.data.blog.topic) || '',
                content: htmlString.toString() || '', // Use converted HTML content
                markDownContent: response?.data?.blog?.context || '', // Keep original markdown
                prevState: htmlString.toString(),
                images: response.data.images_urls
                  ? response.data.images_urls.split(',').filter(Boolean)
                  : [],
                wordCount: wordCount,
                settings: {
                  minLength: response.data?.blog.min_len,
                  maxLength: response.data?.blog.max_len,
                  language: response.data?.blog.language,
                  toneOfVoice: response.data?.blog.tone,
                  industry: response.data.blog.industry,
                  imageCount: response.data.blog.no_of_images,
                },
              });
              // Update HTML dump with converted content
              // handleUpdateHtmlDump(htmlResponse.data.plain_text);
            } catch (conversionError) {
              console.error('Failed to convert content:', conversionError);
              // Fallback: Use the content as-is if conversion fails
              handleUpdateBlogContent({
                id: response.data.id,
                title: response.data.title || '',
                content: response.data.context || '',
                markDownContent: response.data.context || '',
                images: response.data.images_urls
                  ? response.data.images_urls.split(',').filter(Boolean)
                  : [],
                prevState: response.data.context || '',
                wordCount: wordCount,
                settings: {
                  minLength: '0',
                  maxLength: '1000',
                  language: 'English',
                  toneOfVoice: 'Professional',
                  industry: 'General',
                  imageCount: 0,
                },
              });
              handleUpdateHtmlDump(response.data.context);
            }
          }
          if (blogSubId) {
            setIsLoading(true);
            const result = await blogService.getBlogHistoryById(
              parseInt(blogId),
              Number(blogSubId)
            );
            console.log(result);
            const htmlString = await unified()
              .use(remarkParse)
              .use(remarkMdx)
              .use(remarkHtml)
              .process(result?.data?.updated_content);
            handleUpdateBlogContent({
              title: String(blogContent?.title),
              images: blogContent?.images ? blogContent?.images : [],
              wordCount: Number(blogContent?.wordCount),
              markDownContent: String(result?.data.updated_content),
              content: htmlString.toString(),
              prevState: htmlString.toString(),
              settings: {
                minLength: String(blogContent?.settings.minLength),
                maxLength: String(blogContent?.settings.maxLength),
                language: String(blogContent?.settings.language),
                industry: String(blogContent?.settings.industry),
                toneOfVoice: String(blogContent?.settings.toneOfVoice),
                imageCount: Number(blogContent?.settings.imageCount),
              },
            });
          }
        } catch (error) {
          console.error('Failed to fetch blog:', error);
          toast.error('Failed to load blog');
          handleUpdateBlogContent(DEFAULT_BLOG_CONTENT);
        } finally {
          setIsLoading(false);
        }
      } else {
        setIsLoading(false);
        if (!blogContent) {
          handleUpdateBlogContent(DEFAULT_BLOG_CONTENT);
        }
      }
    };

    fetchBlogIfNeeded();
    // if (isEditPanelOpen) {
    //   setVisibleEditModel(true);
    // }
  }, [isSettingBlog, visibleModel, visibleEditModel, isEditPanelOpen]);

  const handleUndo = () => {
    if (quillRef.current) {
      const quill = quillRef.current;

      // Get current content before undo
      const currentContent = quill.root.innerHTML;

      // Perform undo
      quill.history.undo();

      // Check if content is empty after undo
      const newContent = quill.root.innerHTML;

      // If content is empty or just contains empty paragraph tags, restore the previous content
      if (
        !newContent ||
        newContent === '<p><br></p>' ||
        newContent === '<p></p>'
      ) {
        quill.root.innerHTML = currentContent;
        return;
      }

      // Update active formats after undo
      const selection = quill.getSelection();
      if (selection) {
        setActiveFormats(
          quill.getFormat(selection) as {
            [key: string]: boolean | number | string | unknown;
          }
        );
      }
    }
  };

  const handleRedo = () => {
    if (quillRef.current) {
      const quill = quillRef.current;

      // Perform redo
      quill.history.redo();

      // Ensure we have a valid selection after redo
      if (!quill.getSelection()) {
        quill.setSelection(0, 0);
      }

      // Update active formats after redo
      const selection = quill.getSelection();
      if (selection) {
        setActiveFormats(
          quill.getFormat(selection) as {
            [key: string]: boolean | number | string | unknown;
          }
        );
      }
    }
  };

  const handleFormatClick = (
    format: string,
    value?: number | null | string
  ) => {
    if (quillRef.current) {
      const quill = quillRef.current;
      const selection = quill.getSelection();
      if (!selection) {
        quill.setSelection(0, 0);
      }
      const range = quill.getSelection(true);

      const format_handlers: { [key: string]: () => void } = {
        bold: () => {
          const currentFormat = quill.getFormat(range);
          quill.formatText(
            range.index,
            range.length,
            'bold',
            !currentFormat.bold,
            'user'
          );
        },
        italic: () => {
          const currentFormat = quill.getFormat(range);
          quill.formatText(
            range.index,
            range.length,
            'italic',
            !currentFormat.italic,
            'user'
          );
        },
        underline: () => {
          const currentFormat = quill.getFormat(range);
          quill.formatText(
            range.index,
            range.length,
            'underline',
            !currentFormat.underline,
            'user'
          );
        },
        strike: () => {
          const currentFormat = quill.getFormat(range);
          quill.formatText(
            range.index,
            range.length,
            'strike',
            !currentFormat.strike,
            'user'
          );
        },
        link: () => {
          const url = prompt('Enter URL:');
          if (url) {
            let formattedUrl = url;
            if (!/^https?:\/\//i.test(url)) {
              formattedUrl = 'https://' + url;
            }
            quill.formatText(
              range.index,
              range.length,
              'link',
              formattedUrl,
              'user'
            );
          }
        },
        bullet: () => {
          const currentFormat = quill.getFormat(range);
          quill.formatLine(
            range.index,
            range.length,
            'list',
            currentFormat.list === 'bullet' ? false : 'bullet',
            'user'
          );
        },
        ordered: () => {
          const currentFormat = quill.getFormat(range);
          quill.formatLine(
            range.index,
            range.length,
            'list',
            currentFormat.list === 'ordered' ? false : 'ordered',
            'user'
          );
        },
        check: () => {
          const currentFormat = quill.getFormat(range);
          quill.formatLine(
            range.index,
            range.length,
            'list',
            currentFormat.list === 'check' ? false : 'check',
            'user'
          );
        },
        header: () => {
          if (typeof value === 'number' || value === null) {
            quill.formatLine(
              range.index,
              range.length,
              'header',
              value,
              'user'
            );
          }
        },
        indent: () => {
          const currentIndent = (quill.getFormat(range).indent as number) || 0;
          quill.formatLine(
            range.index,
            range.length,
            'indent',
            currentIndent + 1,
            'user'
          );
        },
        color: () => {
          quill.formatText(
            range.index,
            range.length,
            'color',
            value || false,
            'user'
          );
        },
        background: () => {
          quill.formatText(
            range.index,
            range.length,
            'background',
            value || false,
            'user'
          );
        },
        table: () => {
          const rows = prompt('Number of rows:', '3');
          const cols = prompt('Number of columns:', '3');
          if (rows && cols) {
            const numRows = parseInt(rows);
            const numCols = parseInt(cols);
            let table = '<table><tbody>';
            for (let i = 0; i < numRows; i++) {
              table += '<tr>';
              for (let j = 0; j < numCols; j++) {
                table += '<td><br></td>';
              }
              table += '</tr>';
            }
            table += '</tbody></table>';
            const delta = quill.clipboard.convert({ html: table });
            quill.updateContents(delta, 'user');
          }
        },
      };

      const handler = format_handlers[format];
      if (handler) {
        handler();

        // Update active formats after the operation
        const newSelection = quill.getSelection();
        if (newSelection) {
          setActiveFormats(
            quill.getFormat(newSelection) as {
              [key: string]: boolean | number | string | unknown;
            }
          );
        }
      }
    }
  };

  const handleQuillRef = (quill: Quill) => {
    quillRef.current = quill;
  };

  const handleActiveFormatsChange = (formats: {
    [key: string]: boolean | number | string | unknown;
  }) => {
    setActiveFormats(formats);
  };

  const handleWordCountChange = (count: number) => {
    setWordCount(count);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  const editorContent = blogContent || DEFAULT_BLOG_CONTENT;

  return (
    <div
      className={`${
        isEditPanelOpen ? 'justify-center' : 'justify-start'
      } flex items-stretch gap-2 h-full

      `}
    >
      {(!isGenerating || isSettingBlog) && visibleModel ? (
        <ImageDeleteWarning setVisibleModel={() => setVisibleModel(false)} />
      ) : (
        <></>
      )}
      {(!isGenerating || isSettingBlog) && visibleEditModel ? (
        <NewBlogWarning
          setVisibleEditModel={() => setVisibleEditModel(false)}
        />
      ) : (
        <></>
      )}
      {showAuthModal && (
        <CheckAuthModel setVisibleEditModel={setShowAuthModal} />
      )}
      <div className="w-screen bg-background-main flex flex-col h-full rounded-lg">
        <div className={`flex flex-col h-full rounded-lg     `}>
          <div className="flex-none bg-background rounded-lg">
            <BlogHeader
              isEditPanelOpen={isEditPanelOpen}
              isHistoryPanelOpen={isHistoryPanelOpen}
              onEditClick={() => handlEditPanel(true)}
              onHisotryPanelClick={() => setIsHistoryPanelOpen(true)}
            />
          </div>

          <div className="flex-1">
            <main
              className={`relative h-full overflow-hidden ${!isGenerating && (visibleEditModel || visibleModel) ? 'bg-background-overlay/50' : ''}`}
            >
              <div
                className={`max-w-[850px] mx-auto px-4 py-8 overflow-y-auto 
                     
                  `}
              >
                <BlogEditor
                  content={editorContent}
                  onChange={handleUpdateBlogContent}
                  isEditPanelOpen={isEditPanelOpen}
                  onUndo={handleUndo}
                  onRedo={handleRedo}
                  onQuillRef={handleQuillRef}
                  onActiveFormatsChange={handleActiveFormatsChange}
                  onWordCountChange={handleWordCountChange}
                />
              </div>
            </main>
          </div>

          <div className="flex-none bg-background-main rounded-lg">
            <EditToolbar
              isVisible={isEditPanelOpen}
              onFormatClick={handleFormatClick}
              onUndo={handleUndo}
              onRedo={handleRedo}
              wordCount={wordCount}
              activeFormats={activeFormats}
            />
          </div>
        </div>
      </div>
      {isEditPanelOpen && (
        <div className="flex-none rounded-lg w-[400px]">
          <EditPanel onClose={() => handlEditPanel(false)} />
        </div>
      )}
      {isHistoryPanelOpen && (
        <div className="flex-none rounded-lg w-[400px]">
          <HistoryPanel
            isOpen={isHistoryPanelOpen}
            onClose={() => setIsHistoryPanelOpen(false)}
          />
        </div>
      )}
      <div className="fixed lg:hidden bottom-0 left-0 right-0 p-4 bg-background-main border-t flex gap-4 justify-center">
        <div className="w-[50%]">
          <Button
            variant="customSecondary"
            className=" bg-primary  text-white w-full"
            onClick={() => {
              navigator.clipboard.writeText(blogContent?.markDownContent || '');
              toast.success('Copied to clipboard');
            }}
            disabled={isGenerating}
          >
            Copy
          </Button>
        </div>
        <div className="w-[50%]">{<BlogSaveAs />}</div>
        {/* Edit / Save */}
        {/* </Button> */}
      </div>
    </div>
  );
}
